import { Clock } from "lucide-react";

export interface WeeklyScheduleLine {
  dayLabel: string;
  dayShort: string;
  hoursText: string;
  isToday: boolean;
  isClosed: boolean;
}

export interface StoreHoursDisplay {
  summary: string;
  compactStatus: string;
  todayStatus: string;
  nextOpenNotice?: string | null;
  weekHolidayNotice?: string | null;
  weeklySchedule?: WeeklyScheduleLine[];
}

interface Props {
  hoursDisplay?: StoreHoursDisplay | null;
  /** Outside / card: short status only. Inside / detail: full week breakdown. */
  compact?: boolean;
  className?: string;
}

function statusColor(status: string) {
  if (status === "Open") return "text-emerald-600 dark:text-emerald-400";
  if (status.startsWith("Opens ")) return "text-primary";
  return "text-muted-foreground";
}

/** Renders computed store hours for Visit Us cards and store locator detail. */
export function StoreHoursBlock({
  hoursDisplay,
  compact = false,
  className = "",
}: Props) {
  if (!hoursDisplay) return null;

  const compactLine =
    hoursDisplay.compactStatus || hoursDisplay.todayStatus || hoursDisplay.summary;

  if (compact) {
    if (!compactLine) return null;
    return (
      <div className={`space-y-0.5 ${className}`}>
        <div className="flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-primary shrink-0" />
          <span
            className={`text-[11px] font-medium leading-snug ${statusColor(compactLine)}`}
          >
            {compactLine}
          </span>
        </div>
        {hoursDisplay.nextOpenNotice && (
          <p className="text-[10px] text-primary font-medium leading-snug ps-[1.375rem]">
            {hoursDisplay.nextOpenNotice}
          </p>
        )}
      </div>
    );
  }

  const schedule = hoursDisplay.weeklySchedule;

  return (
    <div className={`space-y-2 ${className}`}>
      {hoursDisplay.todayStatus && (
        <p className={`text-sm font-semibold ${statusColor(hoursDisplay.compactStatus || "")}`}>
          {hoursDisplay.todayStatus}
        </p>
      )}

      {hoursDisplay.nextOpenNotice && (
        <p className="text-xs text-primary font-medium">{hoursDisplay.nextOpenNotice}</p>
      )}

      {schedule && schedule.length > 0 ? (
        <div className="space-y-1">
          {schedule.map((day) => (
            <p
              key={day.dayLabel}
              className={`text-xs leading-snug flex items-baseline justify-between gap-3 ${
                day.isToday ? "text-primary font-medium" : "text-muted-foreground"
              }`}
            >
              <span>
                {day.dayLabel}
                {day.isToday && (
                  <span className="text-[10px] font-normal text-muted-foreground ms-1">
                    (today)
                  </span>
                )}
              </span>
              <span className={day.isClosed ? "text-muted-foreground" : "text-foreground/90"}>
                {day.hoursText}
              </span>
            </p>
          ))}
        </div>
      ) : (
        hoursDisplay.summary && (
          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <Clock className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <span>{hoursDisplay.summary}</span>
          </p>
        )
      )}

      {hoursDisplay.weekHolidayNotice && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 leading-snug">
          {hoursDisplay.weekHolidayNotice}
        </p>
      )}
    </div>
  );
}
